import { Injectable } from "@angular/core";
import { BehaviorSubject } from "rxjs";
import { ChatUser, CommonRecentChat, Group } from "../../conversation/models/message.model";

export interface ChatState {
    recentChats: CommonRecentChat[];
    chatUsers: Record<number, ChatUser>; // Use Record for efficient lookups by userId
    groups: Record<number, Group>; // Use Record for efficient lookups by groupId
}

@Injectable({
    providedIn: "root",
})
export class ChatStateService {

    // --- Private State Subjects ---
    private readonly state$$ = new BehaviorSubject<ChatState>({
        recentChats: [],
        chatUsers: {},
        groups: {},
    });

    //TODO:
    //1. Save Recent Chats, Users, Groups in service
    //2. Add methods to get recent chats, users, groups
    //3. Add methods to update recent chats, users, groups,
    //4. Manage service state based on user login/logout
    //5. Add methods to clear recent chats, users, groups on logout

    // --- Public Methods to Modify State ---

    /**
     * Update Chat from Existing Recent Chats
     * This method is used to update the specific chat in the recent chats list.
     * @param chat - The CommonRecentChat object to update.
     * @param pushToTop - If true, the chat will be moved to the top of the recent chats list.
     */
    public updateRecentMessages(chat: CommonRecentChat, pushToTop: boolean): void {
        const currentState = this.getChatState();

        // // update unread message count for new messages which arrive
        // if (!chat.lastMessageSenderIsCurrentUser) {
        //     chat.unreadMessageCount = (chat.unreadMessageCount ?? 0) + 1;
        // }

        // Find the index of the chat to update
        const chatIndex = currentState.recentChats.findIndex((c) => {
            return c.entityId === chat.entityId && c.entityType === chat.entityType;
        });
        // Create a copy of the current recent chats
        const updatedChats = [...currentState.recentChats];
        // If the chat exists, update it
        if (chatIndex !== -1) {
            updatedChats[chatIndex].selected = false;
            updatedChats[chatIndex] = chat;
        } else {
            // If the chat does not exist, add it to the list
            updatedChats.push(chat);
        }
        // If pushToTop is true, move the updated chat to the top of the list
        if (pushToTop) {
            updatedChats.unshift(updatedChats.splice(chatIndex, 1)[0]);
        }
        // Set the updated chats back to the state
        this.updateAndBroadcast({ ...currentState, recentChats: updatedChats });
    }

    /**
     * Set Groups, Users, and Recent Chats in one go.
     * This is useful for initial state hydration.
     */
    public setInitialState(
        recentChats: CommonRecentChat[],
        chatUsers: ChatUser[],
        groups: Group[],
    ): void {
        const currentState = this.getChatState();
        const usersMap = chatUsers.reduce(
            (acc, user) => {
                acc[user.accountId] = user;
                return acc;
            },
            {} as Record<number, ChatUser>,
        );
        const groupsMap = groups.reduce(
            (acc, group) => {
                acc[group.groupId] = group;
                return acc;
            },
            {} as Record<number, Group>,
        );
        this.updateAndBroadcast({
            ...currentState,
            recentChats,
            chatUsers: usersMap,
            groups: groupsMap,
        });
    }

    // --- Core State Management Logic ---

    /**
     * Central method to update state, persist, and broadcast.
     * @param newState The complete new state object.
     */
    private updateAndBroadcast(newState: ChatState): void {
        // 1. Update the main state subject
        this.state$$.next(newState);

        // 2. Recalculate and update derived state (unread count) (Future-proofing)
        // const total = newState.recentChats.reduce((sum, chat) => sum + chat.unreadCount, 0);
        // this.totalUnreadCount$$.next(total);

        // 3. Persist to sessionStorage for other tabs
        // this.saveStateToStorage(newState);

        // 4. Broadcast to other open tabs
        // this.broadcastStateChange(newState);
    }


    getChatState(): ChatState {
        return this.state$$.getValue();
    }

    updateRecentCommonChats(recentChats: CommonRecentChat[]) {
        const currentState = this.getChatState();
        this.updateAndBroadcast({
            ...currentState,
            recentChats: recentChats,
        });
    }

    /**
     * Replaces groups and recent chats in a single emission so subscribers never
     * observe a group without its chat or vice versa.
     */
    setGroupsAndRecentChats(groups: Record<number, Group>, recentChats: CommonRecentChat[]): void {
        const currentState = this.getChatState();
        this.updateAndBroadcast({
            ...currentState,
            groups,
            recentChats,
        });
    }
}
